(ns frontend.view.grid.kyc-grid.events
  (:require
   [frontend.view.grid.kyc-grid.db :as db]
   [frontend.view.grid.kyc-grid.columns :as columns]
   [frontend.react.virtualized.scrollable-grid.common :as grid]
   [frontend.local-storage :as ls]
   [common.schema.registry :as registry]
   [common.localization :as loc]
   [common.util :as cu]
   [frontend.view.modals.events :as modals]
   [potpuri.core :refer [index-by fn-> fn->>]]
   [frontend.state.current-session :as session]
   [frontend.actions.named-view :as named-view-actions]
   [frontend.view.grid.investors-grid.events :as investor-events]
   [frontend.logging :as logging]
   [frontend.utils :as fu]
   [frontend.conn :as conn]
   [promesa.core :as p]
   [frontend.state.permissions :as f-permissions]
   [frontend.view.modals.confirmation.view :as confirmation]
   [frontend.tenant-customizations :as tenant-customizations]
   [frontend.zap-time :as zap-time]
   [frontend.exports.excel :as excel]
   [common.schema.entity-types :as entity-types]
   [common.schema.entities.workspace.kyc.kyc-utils :as kyc-utils]
   [common.timer :as timer]
   [reagent.core :as r]
   [frontend.exports.exceljs.workbook :as ex-workbook]
   [common.timezones :as timezones]
   [common.date-formats :as date-formats]
   [common.schema.entities.workspace.kyc.templates.template-registry :as template-registry]
   [frontend.actions.kyc-profile :as kyc-actions]
   [potpuri.core :as potpuri]))

(defn ls-path
  [& rest]
  (concat
   [(:db/id @session/account)
    (:db/id @session/tenant)
    :kyc-profiles-view-settings]
   rest))

(defn precalculated-schema
  [recalculate?]
  (let [calculated @db/schema
        tenant-schema @session/tenant-schema
        schema
        (cond
          (and (not recalculate?)
               calculated)
          calculated
          :else
          (->>
           (registry/get-schema @session/tenant-schema :entity.type/kyc-profile)
           (remove
            (fn [[k v]]
              (let [grid-visibility (->> v
                                         :field/default-visibility
                                         :grid)]
                (or (nil? grid-visibility)
                    (= grid-visibility :editor.type/none)
                    (if (or (not= (:field/data-type v) :data.type/currency-symbol)
                            (not (:field/secondary-field? v)))
                      (not (f-permissions/field-read-access? k :entity.type/kyc-profile nil))
                      (not (f-permissions/field-read-access? (:field/related-fields v)
                                                             :entity.type/kyc-profile
                                                             nil)))
                    (and (= k :kyc-profile/screening-status)
                         (not (session/screening?)))

                    (and (contains? #{:kyc-profile/calc-blocking-status} k)
                         (not (session/screening?)))))))
           (sort
            (fn [[attribute-a _] [attribute-b _]]
              (cond ;; show name first
                (= :kyc-profile/name attribute-a)
                -1
                (= :kyc-profile/name attribute-b)
                1
                :else
                (compare (let [fs (registry/get-field-schema tenant-schema attribute-a)]
                           (loc/field-name attribute-a fs))
                         (let [fs (registry/get-field-schema tenant-schema attribute-b)]
                           (loc/field-name attribute-b fs))))))
           (doall)))
        investor-schema
        (->> (investor-events/precalculated-schema recalculate?)
             (map (fn [[k v]]
                    [(if (vector? k)
                       (into [:kyc-profile/target] k)
                       [:kyc-profile/target k]) v]))
             (sort-by (fn [[field-path _]]
                        (let [k (last field-path)]
                          (let [fs (registry/get-field-schema tenant-schema k)]
                            (loc/field-name k fs)))))
             (doall))
        schema (if (and (not recalculate?)
                        calculated)
                 schema
                 (concat schema investor-schema))]
    (when (or recalculate?
              (not calculated))
      (reset! db/schema schema))
    schema))

;;precalculate for performance reasons
(defn keys-and-schemas
  []
  (precalculated-schema false))

(defn kyc-profiles-column-keys
  []
  (let [schemas (keys-and-schemas)
        col-keys (concat
                  (when (session/is-admin?) [:db/id])
                  (map first schemas))]
    col-keys))

(def default-kanban-attributes [:kyc-profile/name :kyc-profile/status])

(defn visible-keys-and-schemas
  []
  (let [{:keys [attributes footer-1 footer-2]} @db/card-settings
        visible-attributes
        (set (concat @db/selected-columns
                     attributes
                     default-kanban-attributes
                     [footer-1 footer-2
                      (entity-types/type->owner-attribute :entity.type/kyc-profile)]))]
    (->> (keys-and-schemas)
         (filter (fn [[k _fs]]
                   (some #{k} visible-attributes))))))

(declare expanded? toggle-expand!)

;;necessary arguments to use common grid functions
(defn setup-args
  []
  {:state db/kyc-profiles
   :entity-type :entity.type/kyc-profile
   :modal-state db/modal-state
   :selected-columns-state db/selected-columns
   :ordered-columns-state db/ordered-columns
   :filters-state db/kyc-profile-filters
   :view-settings-state db/view-settings
   :column-widths-state db/column-widths
   :selected-pipeline nil
   :ls-path ls-path
   :kanban? true
   :kanban-args {:default-grouped-by  :kyc-profile/status
                 :kanban-state        db/kanban
                 :grouped-by-state    db/grouped-by
                 :card-settings-state db/card-settings
                 ;;for long card view:
                 :default-attributes  default-kanban-attributes
                 :expanded-state      db/expanded
                 :sorted-kanban-entities-state db/sorted-kanban-entities
                 :lists-state         db/lists
                 :expanded?           expanded?
                 :toggle-expand!      toggle-expand!}
   :column-keys kyc-profiles-column-keys
   :default-columns (columns/get-default-columns)
   :mandatory? columns/mandatory?
   :visible-keys-and-schemas-fn visible-keys-and-schemas})

(defn expanded?
  [entity-id]
  (let [card-mode (or (:card-mode @db/kanban)
                      :short-card)
        individual (get @db/expanded entity-id)]
    (or (and (= card-mode :short-card)
             individual)
        (and (= card-mode :long-card)
             (not individual)))))

(defn toggle-expand!
  [entity-id]
  (let [expanded? (get @db/expanded entity-id)]
    (if expanded?
      (swap! db/expanded dissoc entity-id)
      (swap! db/expanded assoc entity-id true))))

(defn resolve-column-key-universe
  []
  (->> (grid/resolve-column-key-universe (setup-args))
       (remove (fn [column-key]
                 (and (= column-key :kyc-profile/screening-status)
                      (not (session/screening?)))))))

(defn resolve-column-keys
  []
  (grid/resolve-column-keys (setup-args)))

(defn cleanup
  []
  (when-let [tracker (:tracker-1 @db/kyc-profiles)]
    (r/dispose! tracker))
  (when-let [tracker (:tracker-2 @db/kyc-profiles)]
    (r/dispose! tracker))
  (when-let [tracker (:tracker-3 @db/kyc-profiles)]
    (r/dispose! tracker))

  (swap! db/kyc-profiles dissoc
         :loaded
         :schema
         :entities
         :entity-types
         :view-settings
         :sorted-grid-entities
         :search
         :kanban
         :sorted-kanban-entities
         :selected
         :grid-entities))

(defn toggle-quick-filters!
  []
  (swap! db/kyc-profiles update :show-quick-filters? not))

(defn system-views
  []
  (concat [{:named-view/settings {:selected-columns (columns/get-default-columns)
                                  :ordered-columns  (columns/get-ordered-default-columns)
                                  :sort-by          :asset/name
                                  :sort-direction   "ASC"
                                  :view-mode        (if (tenant-customizations/capman?)
                                                      :grid-mode
                                                      :board-mode)
                                  :grouped-by       :kyc-profile/status}
            :named-view/name (loc/tr :all-kyc-profiles)
            :disable-delete? true
            :db/id "named-view-all-kyc-profiles"}]
          (when-not (tenant-customizations/capman?)
            [{:named-view/settings {:selected-columns (columns/get-default-columns)
                                    :ordered-columns  (columns/get-ordered-default-columns)
                                    :sort-by          :asset/name
                                    :sort-direction   "ASC"
                                    :view-mode        :grid-mode}
              :named-view/name (loc/tr :list-kyc-profiles)
              :disable-delete? true
              :db/id "named-view-list-kyc-profiles"}])
          [{:named-view/settings {:selected-columns (columns/get-default-columns)
                                  :sort-by          :asset/name
                                  :sort-direction   "ASC"
                                  :view-mode        :grid-mode
                                  :filters          {:kyc-profile/our-contact
                                                     {:operand :operand/any-of
                                                      :lower-bound-value #{(:db/id
                                                                            @session/account)}}}}
            :named-view/name (loc/tr :my-kyc-profiles)
            :disable-delete? true
            :db/id "named-view-my-kyc-profiles"}
           {:named-view/settings {:selected-columns (columns/get-default-columns)
                                  :ordered-columns  (columns/get-ordered-default-columns)
                                  :sort-by          :asset/name
                                  :sort-direction   "ASC"
                                  :view-mode        :grid-mode
                                  :filters          {:kyc-profile/status {:operand :operand/any-of
                                                                          :lower-bound-value
                                                                          #{:kyc-status/expired}}}}
            :named-view/name (loc/tr :expired-kyc-profiles)
            :disable-delete? true
            :db/id "named-view-kyc-profile-pipeline"}]))

(defn system-views-by-id
  []
  (->> (system-views)
       (map (fn [v]
              [(:db/id v) v]))
       (into {})))

(defn named-views
  []
  (concat (system-views)
          (->> @session/all-named-views
               (cu/only-entity :named-view/view :kyc-grid)
               (sort-by :common/created-on))))

(defn named-view-by-id
  [id]
  (or (get @session/entities id)
      (get (system-views-by-id) id)))

(defn switch-mode!
  [current-mode]
  (let [new-mode (if (= current-mode :grid-mode) :board-mode :grid-mode)]
    (swap! db/view-settings assoc :view-mode new-mode)
    (ls/update-in (ls-path) assoc :view-mode new-mode)))

(defn setup-column-state!
  [view-settings]
  (reset! db/view-settings view-settings)
  (when (nil? @db/selected-columns)
    (let [default-view-settings (->> (named-views)
                                     first
                                     :named-view/settings)]
      (reset! db/view-settings default-view-settings)
      (ls/set-in (ls-path) default-view-settings)))
  (grid/recalculate (assoc (setup-args) :re-sort? true)))

(defn load-view!
  [named-view] ;;named-view entity
  (let [{:keys [named-view/settings]} named-view]
    (ls/set-in (ls-path) settings)
    (setup-column-state! settings)))

(defn kyc-profile-updates
  []
  (let [kyc-profiles @session/all-kyc-profiles]
    (if-not (session/screening?)
      (swap! db/kyc-profiles assoc :grid-entities kyc-profiles)

      (let [screening-statuses @session/kyc-profile-screening-statuses

            kyc-profiles-screening-label-fixed
            (->> kyc-profiles
                 (map (fn [kyc-profile]
                        (let [screening-status (:kyc-profile/screening-status kyc-profile)]
                          ; Initially kyc profiles do not have any status set, it is same as explicit :screening-status/auto
                          (if (or (not screening-status)
                                  (= screening-status :screening-status/auto))
                            (if-let [screening-status-data (get screening-statuses (:db/id kyc-profile))]
                              (let [active-screening? (:active? screening-status-data)
                                    screening-status-label (if active-screening?
                                                             (loc/tr :screening-status/auto-active)
                                                             (loc/tr :screening-status/auto-inactive))]
                                (assoc kyc-profile :kyc-profile/screening-status screening-status-label))
                              kyc-profile)
                            kyc-profile)))))]
        (swap! db/kyc-profiles assoc :grid-entities kyc-profiles-screening-label-fixed)))))

(defn permission-updates
  []
  (let [current-permissions @session/current-permissions]
    (when current-permissions
      (precalculated-schema true))))

(defn trigger-recalculation
  []
  (let [permissions-trigger @db/schema
        entities-trigger @db/grid-entities]
    (when (or permissions-trigger entities-trigger)
      (fu/timeout 10
                  (fn []
                    (grid/recalculate (assoc (setup-args) :stale-sort? true))
                    (swap! db/kyc-profiles assoc :loaded true)
                    (deref db/kyc-profiles))))))

(defn setup-without-screening
  [closed-atom?]
  (when-not @closed-atom?
    (if (empty? (ls/get-in (ls-path)))
      (load-view! (first (named-views)))
      (reset! db/view-settings (ls/get-in (ls-path))))
    (swap! db/kyc-profiles assoc
           :tracker-1 (r/track! kyc-profile-updates)
           :tracker-2 (r/track! permission-updates)
           :tracker-3 (r/track! trigger-recalculation))
    (setup-column-state! (ls/get-in (ls-path)))))

(defn setup-with-screening
  [closed-atom?]
  (let [kyc-profile-ids (->> @session/kyc-profiles
                             (map :db/id))]
    (-> (p/all [(conn/sendp! :kyc-profile/get-screening-statuses
                             {:kyc-profile-ids kyc-profile-ids})
                (conn/sendp! :screening-profile/get-all
                             {})])
        (p/then
         (fn [[screening-statuses-resp screening-profiles-resp]]
           (let [statuses (-> screening-statuses-resp
                              :body
                              :statuses)
                 screening-profiles-by-id (->> screening-profiles-resp
                                               :body
                                               :screening-profiles
                                               (potpuri/index-by :db/id))]
             (swap! session/session assoc :kyc-profile-screening-statuses statuses)
             (swap! session/entities update merge screening-profiles-by-id)
             (setup-without-screening closed-atom?))))
        (p/catch (fn [err]
                   (js/console.log "Error loading statuses or screening profiles " err))))))

(defn setup
  [closed-atom?]
  (if (session/screening?)
    (setup-with-screening closed-atom?)
    (setup-without-screening closed-atom?)))

(defn save-current-view!
  [name]
  (let [settings @db/view-settings
        msg {:named-view/name     name
             :named-view/view     :kyc-grid
             :named-view/settings settings}]
    (named-view-actions/*create-named-view->
     msg
     identity
     (fn [& a] ;;TODO show error message
       (logging/error "create-named-view failed" a)))))

(defn target-asset-in
  [company-id]
  (let [target-asset-oppos (->> @session/all-oppos
                                (remove :common/disabled?)
                                (cu/only-entity :oppo/target-asset company-id))
        target-asset-holdings (->> @session/all-holdings
                                   (remove :common/disabled?)
                                   (cu/only-entity :holding/target-asset company-id))
        all-target-asset-entities (concat target-asset-oppos
                                          target-asset-holdings)]
    all-target-asset-entities))

(defn mass-delete!
  []
  (-> (conn/sendp! :kyc-profile/mass-action
                   {:action :delete-kyc-profiles
                    :kyc-profile-ids (grid/get-selected-items (setup-args))})
      (p/then (fn [_resp]
                (swap! db/kyc-profiles assoc :selected {})))
      (p/catch (fn [e]
                 (logging/error "Unable to delete selected kyc-profiles" e)))))

(defn delete-selected-clicked
  [_]
  (modals/open-modal!
   confirmation/modal-key
   {:body          [:div
                    [:p (loc/tr :confirm-delete-selected)]]
    :confirm-text  (loc/tr :confirm-delete-selected-button)
    :checkbox-text (loc/tr :confirm-delete-selected-checkbox)
    :on-confirm    #(mass-delete!)}))

(defn expand-all!
  []
  (swap! db/kanban assoc :card-mode :long-card)
  (reset! db/expanded {}))

(defn collapse-all!
  []
  (swap! db/kanban assoc :card-mode :short-card)
  (reset! db/expanded {}))

(defn open-card-settings!
  []
  (swap! db/kanban assoc :card-settings? true))

(defn close-card-settings!
  []
  (swap! db/kanban dissoc :card-settings?))

(defn approved-process?
  [process]
  (contains? #{:kyc-process.status/approved :kyc-process.status/approved-modified}
             (:kyc-process/status process)))

(defn remove-attachment-questions
  [questions-meta]
  (->> questions-meta
       (remove (fn [[_q question-meta]]
                 (contains? #{:data.type/file :data.type/confirm} (:data/type question-meta))))))

(defn resolve-investor-info-from-process
  [process]
  (let [kyc-profile (some->> @session/all-kyc-profiles
                             (filter (fn [kp]
                                       (some #{(:db/id process)} (:kyc-profile/kyc-processes kp))))
                             (first))
        investor (session/get-entity (:kyc-profile/target kyc-profile))]
    (when investor
      (select-keys investor [:investor/name :db/id :common/external-id]))))

(defn resolve-kyc-display-datas
  [export-questions answers]
  {:display-data (->> answers
                      (keep
                       (fn [[q v]]
                         (when-let [disp (kyc-utils/display-string q (get export-questions q) v)]
                           [q (fu/if-bigdec-then-float disp)])))
                      (into {}))})

(defn resolve-template-process-answers
  [processes template-types export-schema]
  (->> processes
       (filter (fn [process]
                 (contains? (set template-types) (:kyc-process/template process))))
       (keep (fn [process]
               (let [answers (:kyc-process/answers process)]
                 (when (seq answers)
                   (merge answers
                          (resolve-investor-info-from-process process)
                          (resolve-kyc-display-datas export-schema answers))))))))

(defn resolve-question-process-answers
  [processes export-questions export-schema]
  (->> processes
       (mapcat (fn [process]
                 (when-let [process-answers (:kyc-process/answers process)]
                   (let [answer-peps (some->> export-questions
                                              (keep (fn [q]
                                                      (get process-answers q)))
                                              (first))]
                     (some->> answer-peps
                              (keep (fn [pep-answers]
                                      (when (seq pep-answers)
                                        [process pep-answers]))))))))
       (remove nil?)
       (map (fn [[process question-answer]]
              (merge question-answer
                     (resolve-investor-info-from-process process)
                     (resolve-kyc-display-datas export-schema question-answer))))
       (doall)))

(defn resolve-process-answers
  [processes export-schema]
  (let [export-templates (:export-templates export-schema)
        export-questions (:export-questions export-schema)]
    (cond export-templates ;; legal entity or natural person
          (resolve-template-process-answers processes export-templates export-schema)
          export-questions ;; UBO or PEP
          (resolve-question-process-answers processes export-questions export-schema)
          :else
          (logging/error "Could not resolve KYC export schema type " export-schema))))

(defn resolve-schema-export-question-kws
  [schema]
  (->> schema
       (remove-attachment-questions)
       (map first)))

(defn last-approved-process
  [profile]
  (->> profile
       :kyc-profile/kyc-processes
       (keep session/get-entity)
       (filter approved-process?)
       (sort-by :common/created-on)
       (last)))

(defn get-export-sheet-names-data-and-column-keys
  "Returns list of [sheet-name data-rows sorted-column-keywords]"
  [tenant tenant-schema export-schemas keys-and-schemas profiles]
  (let [profile-datas
        (->>
         profiles
         (map
          (fn [profile]
            (merge
             profile
             (->> keys-and-schemas
                  (map
                   (fn [[k fs]]
                     [k
                      (fu/display-data tenant-schema tenant k fs profile :skip-multiplier? true)]))
                  (into {}))))))
        column-keys (->> keys-and-schemas
                         (map first))
        last-approved-processes (->> profile-datas
                                     (map :db/id)
                                     (keep session/get-entity)
                                     (remove :common/disabled?)
                                     (keep last-approved-process))]
    (concat
     [["KYC profiles" profile-datas column-keys]]
     (->> export-schemas
          (map (fn [export-schema]
                 (let [export-questions
                       (dissoc export-schema :export-name :export-questions :export-templates)]
                   [(:export-name export-schema)
                    (resolve-process-answers last-approved-processes export-schema)
                    (concat [:investor/name]
                            (resolve-schema-export-question-kws export-questions))])))))))

(defn process-next-sheet!
  [{:keys [wb export-entities timer
           export-sheet-datas-atom progress-atom progress-rows-atom cancel-atom]
    :as   args}]
  (let [next-sheet (first @export-sheet-datas-atom)]
    (if-not next-sheet
      (let [todays-date (date-formats/format-millis
                         (zap-time/now)
                         (or (:tenant/date-format @session/tenant) (loc/tr :date-format))
                         (or (:tenant/timezone @session/tenant) timezones/default-timezone))
            file-name (loc/tr :export-filename-generic [(loc/tr :entity.type/kyc-profile) todays-date])]
        (ex-workbook/save-workbook wb file-name)
        (reset! progress-atom 1))
      (let [[sheet-name sheet-data sheet-columns] next-sheet]
        (reset! export-sheet-datas-atom (rest @export-sheet-datas-atom))
        (excel/export-sheet-entities
         (assoc args
                :skip-progress-modal? true
                :processed-rows-start-count @progress-rows-atom
                :on-sheet-completed! (fn []
                                       (fu/timeout 150
                                                   (fn []
                                                     (process-next-sheet! args))))
                :sheet (ex-workbook/add-sheet wb sheet-name)
                :column-attributes sheet-columns
                :data-map-rows sheet-data))))))

(defn export-kyc-data
  []
  (let [progress-atom (r/atom 0)
        progress-rows-atom (atom 0)
        cancel-atom (atom false)
        timer (timer/create-timer)
        wb (ex-workbook/create-workbook)
        profile-keys (keys-and-schemas)
        sorted-entities (:sorted-kanban-entities @db/kyc-profiles)
        selected-item-ids (when-not (= (:selected @db/kyc-profiles) :all)
                            (some->> (:selected @db/kyc-profiles)
                                     (map (fn [[k s]]
                                            (when s
                                              k)))))
        export-entities (if (and (coll? selected-item-ids)
                                 (seq selected-item-ids))
                          (->> sorted-entities
                               (filter (fn [entity]
                                         (some #{(:db/id entity)} selected-item-ids))))
                          sorted-entities)
        total-rows (->> export-entities
                        (keep second)
                        (map count)
                        (apply +))
        tenant @session/tenant
        tenant-schema @session/tenant-schema
        tenant-template-kws (->> tenant
                                 :tenant/active-kyc-templates)
        export-schemas (template-registry/resolve-export-schemas tenant-template-kws)
        export-questions-lookup (->> export-schemas
                                     (apply merge))
        export-sheet-datas-atom
        (atom (get-export-sheet-names-data-and-column-keys tenant
                                                           tenant-schema
                                                           export-schemas
                                                           profile-keys
                                                           export-entities))
        args {:progress-atom progress-atom
              :progress-rows-atom progress-rows-atom
              :export-sheet-datas-atom export-sheet-datas-atom
              :cancel-atom cancel-atom
              :timer timer
              :wb wb
              :progress-total-count total-rows
              :resolve-schema (fn [k]
                                (or (when-let [question-schema (get export-questions-lookup k)]
                                      question-schema)
                                    (registry/get-field-schema tenant-schema k)))}]
    (excel/open-export-progress-modal progress-atom cancel-atom)
    (process-next-sheet! args)))
